// ./src/hooks/useTemplateCategoryItems.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  TemplateCategoryItem,
  TemplateCategoryItemService,
  TemplateCategoryItemFilter,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseTemplateCategoryItemsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<TemplateCategoryItemFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;

  userId?: string | null;
}

export interface UseTemplateCategoryItemsReturn {
  // Data state
  templateCategoryItems: TemplateCategoryItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<TemplateCategoryItemFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  selectedTemplateCategoryItem: TemplateCategoryItem | null;

  // Core operations (matching API method names exactly)
  list: (
    filter?: Partial<TemplateCategoryItemFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  ensureTemplateCategoryItems: (
    templateCategoryItems: TemplateCategoryItem[] | null | undefined
  ) => void;

  // Search and filter operations
  setFilters: (filters: Partial<TemplateCategoryItemFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedTemplateCategoryItem: (
    templateCategoryItem: TemplateCategoryItem | null
  ) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [];

export const useTemplateCategoryItems = (
  options: UseTemplateCategoryItemsOptions = {}
): UseTemplateCategoryItemsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [templateCategoryItems, setTemplateCategoryItems] = useState<
    TemplateCategoryItem[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<TemplateCategoryItemFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [selectedTemplateCategoryItem, setSelectedTemplateCategoryItem] =
    useState<TemplateCategoryItem | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => templateCategoryItems.length === 0 && !loading,
    [templateCategoryItems.length, loading]
  );

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    console.log("Resetting all the template category items");
    setTemplateCategoryItems([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setSelectedTemplateCategoryItem(null);
  }, []);

  // List template category items (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<TemplateCategoryItemFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setTemplateCategoryItems([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await TemplateCategoryItemService.list(
          filter as TemplateCategoryItemFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newTemplateCategoryItems = Array.isArray(result.data)
            ? result.data
            : [];

          if (isAppending) {
            setTemplateCategoryItems((prev) => {
              const existingIds = new Set(prev.map((c) => c.id));
              const uniqueNewTemplateCategoryItems =
                newTemplateCategoryItems.filter((c) => !existingIds.has(c.id));
              return [...prev, ...uniqueNewTemplateCategoryItems];
            });
          } else {
            setTemplateCategoryItems(newTemplateCategoryItems);
          }

          setHasMore(newTemplateCategoryItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load template category items";
          setLastError(errorMessage);
          if (!isAppending) setTemplateCategoryItems([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load template category items";
        setLastError(errorMessage);
        if (!isAppending) setTemplateCategoryItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Ensure template category items exist in the list (add if not already present)
  const ensureTemplateCategoryItems = useCallback(
    (
      templateCategoryItemsToEnsure: TemplateCategoryItem[] | null | undefined
    ) => {
      if (
        !templateCategoryItemsToEnsure ||
        templateCategoryItemsToEnsure.length === 0
      ) {
        return;
      }

      setTemplateCategoryItems((prev) => {
        // Create a set of existing template category item IDs for fast lookup
        const existingIds = new Set(prev.map((c) => c.id));

        // Filter out template category items that already exist
        const newTemplateCategoryItems = templateCategoryItemsToEnsure.filter(
          (templateCategoryItem) =>
            templateCategoryItem.id && !existingIds.has(templateCategoryItem.id)
        );

        // If no new template category items to add, return previous state
        if (newTemplateCategoryItems.length === 0) {
          return prev;
        }

        // Add new template category items to the list
        return [...prev, ...newTemplateCategoryItems];
      });
    },
    []
  );

  // Load more template category items
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh template category items
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  useEffect(() => {
    if (autoLoad && isAuthenticated && loadedForUserId != userId) {
      setLoadedForUserId(userId);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [
    autoLoad,
    isAuthenticated,
    userId,
    loadedForUserId,
    list,
    pageSize,
    initialSort,
  ]);

  return {
    templateCategoryItems,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    selectedTemplateCategoryItem,
    list,
    loadMore,
    refresh,
    ensureTemplateCategoryItems,
    setFilters,
    setSortBy,
    setSelectedTemplateCategoryItem,
    reset,
    lastError,
    clearError,
  };
};
